<template>
    <div class="card">
        <div class="card-header">Interstate Endorsement Information</div>
        <div class="card-body">
            <div class="alert alert-info" role="alert">
                <b-row>
                    <b-col>
                        Please note: Your registration will be calculated on either your test results date or your most recent employment date,
                        whichever is later.
                    </b-col>
                </b-row>
            </div>
            <div class="alert alert-danger" v-if="isDirty() && $v.application.endorsement.hasWorkOrTest.$invalid" role="alert">
                <b-row>
                    <b-col>
                        You need to have tested within the past 24 months from the endorsed state OR been employed at a facility in the last 24
                        months in the state where you are currently endorsed to complete the interstate endorsement application.
                    </b-col>
                </b-row>
            </div>

            <b-row>
                <b-col cols="6">
                    <BaseSelector v-model="application.endorsement.state"
                                  :v="$v.application.endorsement.state"
                                  label="State"
                                  slug="state"
                                  :options=opts.stateOptions
                                  :tip="'Please indicate the state you are endorsing from.'"
                    />
                </b-col>
                <b-col cols="6">
                    <BaseTextInput v-model="application.endorsement.certificateNumber"
                                   :v="$v.application.endorsement.certificateNumber"
                                   slug="certificateNumber"
                                   label="State Certificate Number"
                    />
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="4">
                    <BaseRadioInput v-model="application.endorsement.workPeriod"
                                    :v="$v.application.endorsement.workPeriod"
                                    slug="workPeriod"
                                    label="In the past 24 months, were you working at a facility in the state you are currently endorsing from?"
                                    :options=opts.yesNoBoolOptions />
                </b-col>
                <b-col cols="4">
                    <BaseRadioInput v-model="application.endorsement.testPeriod"
                                    :v="$v.application.endorsement.testPeriod"
                                    slug="testPeriod"
                                    label="Did you test within the past 24 months in the state you are endorsed by?"
                                    :options=opts.yesNoBoolOptions />
                </b-col>
                <b-col cols="4" v-if="application.endorsement.testPeriod === true">
                    <BaseDateOfBirthInput v-model="application.endorsement.testDate"
                                   :v="$v.application.endorsement.testDate"
                                   slug="testDate"
                                   label="Date Tested in Endorsing State"
                                   tip="Format: MM/DD/YYYY"
                    />
                </b-col>
            </b-row>
          <div v-if="application.endorsement.workPeriod">
            <b-row>
                <b-col cols="6">
                    <BaseTextInput v-model="application.facility.name"
                                   :v="$v.application.facility.name"
                                   slug="facilityName"
                                   label="Facility Name"
                    />
                </b-col>
                <b-col cols="6" class="pt-4">
                    <BaseCheckboxInput label="I am working at this facility through a staffing agency."
                                       v-model="application.facility.staffingAgency"
                                       :v="$v.application.facility.staffingAgency"
                                       slug="staffingAgency"
                                       setId="staffingAgency"
                    />
                </b-col>
            </b-row>
            <h2>
                Facility Address:
            </h2>
            <b-row>
                <b-col cols="8">
                    <BaseTextInput v-model="application.facility.address.streetAddress"
                                   :v="$v.application.facility.address.streetAddress"
                                   slug="facilityStreetAddress"
                                   label="Street Address"
                                   tip="Please include apartment number or PO Box number if relevant."
                    />
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="4">
                    <BaseTextInput v-model="application.facility.address.city"
                                   :v="$v.application.facility.address.city"
                                   slug="city"
                                   label="City"
                    />
                </b-col>
                <b-col cols="4">
                    <BaseSelector v-model="application.facility.address.state"
                                  :v="$v.application.facility.address.state"
                                  label="State"
                                  slug="state"
                                  :options=opts.stateOptions
                    />
                </b-col>
                <b-col cols="4">
                    <BaseMaskedTextInput v-model="application.facility.address.postalCode"
                                         :v="$v.application.facility.address.postalCode"
                                         slug="postalCode"
                                         label="ZIP"
                                         mask="#####-####"
                    />
                </b-col>
            </b-row>
          </div>
        </div>
    </div>
</template>

<script>
    import BaseTextInput from "@/components/templates/input/BaseTextInput"
    import BaseSelector from "@/components/templates/input/BaseSelector"
    import BaseRadioInput from "@/components/templates/input/BaseRadioInput";
    import { required, requiredIf, minLength, maxLength } from 'vuelidate/lib/validators'
    import {commonOptions} from "@/mixins/commonOptions";

export default {
    components:{
        BaseTextInput,
        BaseSelector,
        BaseRadioInput,
    },

    mixins: [
        commonOptions
    ],
    props:{
        application: [Object]
    },
    validations() {
        return {
            application: {
                endorsement: {
                    hasWorkOrTest: {
                        reqWorkOrTest() {
                            return (this.application.endorsement.testPeriod != null && this.application.endorsement.workPeriod != null)
                                && (this.application.endorsement.testPeriod || this.application.endorsement.workPeriod)
                        }
                    },
                    testPeriod: {required},
                    testDate: { required: requiredIf(function () {
                            return this.application.endorsement.testPeriod === true
                        })
                    },
                    workPeriod: {required},
                    state: {required},
                    certificateNumber: {required},
                },
                facility: {
                    staffingAgency: { required:false },
                    name: { required: requiredIf(function() { return this.hasWorkPeriod() }) },
                    address:{
                        required: true,
                        streetAddress: { required: requiredIf(function() { return this.hasWorkPeriod() }) },
                        city: { required: requiredIf(function() { return this.hasWorkPeriod() }) },
                        state: { required: requiredIf(function() { return this.hasWorkPeriod() }) },
                        postalCode: {
                            required: requiredIf(function() { return this.hasWorkPeriod() }),
                            minLength: minLength(5),
                            maxLength: maxLength(10)
                        }
                    }
                }
            },
            form: ["application"]
        }
    },
    methods:{
        validate(){
            this.$v.form.$touch();
            let isValid = !this.$v.form.$invalid;
            this.$emit('on-validate', this.$data.application, isValid);
            return isValid;
        },
        isDirty() {
            return this.$v.application.endorsement.testPeriod.$dirty && this.$v.application.endorsement.workPeriod.$dirty
        },
        hasWorkPeriod() {
            return this.workPeriod != null && this.workPeriod === true
        }
    },
}
</script>